import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, PlusIcon } from '@itsrever/design-system'
import {
  Condition,
  ConditionField,
  RULE_CONTEXT_FIELDS,
  RuleContext,
  newCondition,
  toConditions,
  toWhens
} from '@/domain/warehouses'
import {
  AddCondition,
  OpenKey,
  OptionsPicker,
  RuleRow,
  SetOpenKey,
  bindOpen,
  newRuleKey,
  ruleStatus,
  savedKey,
  useRuleOrder
} from '@/ui/components/rule-list'
import {
  ConditionPill,
  LockedPill,
  useFieldGroups
} from '@/ui/components/warehouses/condition-pill'
import { equalJSON } from '@/utils'

const { Button, Card } = Base

// The rule rows reuse the routing rules' copy: numbering, actions and condition pickers.
const NS = 'warehouses.rules'

export interface Column<R> {
  label: string
  className: string
  render: (
    rule: R,
    update: (rule: R) => void,
    cell: { id: string; n: number; openKey: OpenKey; setOpenKey: SetOpenKey }
  ) => ReactNode
}

interface Keyed<R> {
  key: string
  rule: R
}

// Rules whose conditions all must match, in order. Rows are keyed here: the stored rules carry no id.
export function RuleTable<R extends { whens: string[] }>({
  label,
  rules,
  saved,
  context,
  readOnly,
  columns = [],
  addLabel,
  emptyText,
  newRule,
  onChange
}: {
  label: string
  rules: R[]
  // The stored rules, which mark the rows that are new or edited.
  saved: R[]
  context: RuleContext
  readOnly: boolean
  columns?: Column<R>[]
  addLabel: string
  emptyText?: string
  newRule: () => R
  onChange: (rules: R[]) => void
}) {
  const [openKey, setOpenKey] = useState<OpenKey>(null)
  const [order, setOrder] = useState<{ saved: R[]; keys: string[] }>({
    saved,
    keys: []
  })
  // A save, a discard or a refetch replaces the rules; their rows then get the keys of the stored ones.
  const fresh =
    rules === saved ||
    order.saved !== saved ||
    order.keys.length !== rules.length
  const keyed: Keyed<R>[] = rules.map((rule, i) => ({
    rule,
    key: fresh ? savedKey(i) : order.keys[i]
  }))
  const setRules = (next: Keyed<R>[]) => {
    setOrder({ saved, keys: next.map((row) => row.key) })
    onChange(next.map((row) => row.rule))
  }
  const status = (row: Keyed<R>) =>
    ruleStatus(row, saved, (r, original) => equalJSON(r.rule, original))
  const rowProps = useRuleOrder(keyed, setRules)
  const update = (key: string) => (rule: R) =>
    setRules(keyed.map((row) => (row.key === key ? { key, rule } : row)))
  const editable = !readOnly

  return (
    <Card className="overflow-hidden" aria-label={label}>
      <Header columns={columns} />
      {rules.length === 0 && emptyText ? (
        <p className="p-4 text-sm text-neutral-500">{emptyText}</p>
      ) : null}
      <ul>
        {keyed.map((row, index) => (
          <RuleRow
            key={row.key}
            {...rowProps(row, index)}
            ns={NS}
            status={status(row)}
            highlighted={false}
            dimmed={false}
            editable={editable}
            outcome={columns.map((column) => (
              <span key={column.label} className={column.className}>
                {column.render(row.rule, update(row.key), {
                  id: row.key,
                  n: index + 1,
                  openKey,
                  setOpenKey
                })}
              </span>
            ))}
            onDelete={() => setRules(keyed.filter((r) => r.key !== row.key))}
          >
            <RuleConditions
              id={row.key}
              n={index + 1}
              whens={row.rule.whens}
              fields={RULE_CONTEXT_FIELDS[context]}
              editable={editable}
              openKey={openKey}
              setOpenKey={setOpenKey}
              onChange={(whens) => update(row.key)({ ...row.rule, whens })}
            />
          </RuleRow>
        ))}
      </ul>
      {editable ? (
        <div className="border-0 border-t border-solid border-neutral-100 px-3 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const key = newRuleKey()
              setRules([...keyed, { key, rule: newRule() }])
              setOpenKey(`${key}:add`)
            }}
          >
            <PlusIcon color="currentColor" className="size-4" />
            {addLabel}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}

function Header<R>({ columns }: { columns: Column<R>[] }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-4 border-0 border-b border-solid border-neutral-200 bg-neutral-100 py-2.5 pl-3 pr-4 text-xs leading-5 text-neutral-600">
      <span className="w-10 text-right">#</span>
      <span className="flex-1">{t(`${NS}.column_when`)}</span>
      {columns.map((column) => (
        <span key={column.label} className={column.className}>
          {column.label}
        </span>
      ))}
      <span className="w-8" />
    </div>
  )
}

const RuleConditions: React.FC<{
  id: string
  n: number
  whens: string[]
  fields: ConditionField[]
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (whens: string[]) => void
}> = ({ id, n, whens, fields, editable, openKey, setOpenKey, onChange }) => {
  const { t } = useTranslation()
  const fieldGroups = useFieldGroups(fields)
  // Undefined for a rule the builder can't show: it stays as written.
  const conditions = useMemo(() => toConditions(whens), [whens])
  const setConditions = (next: Condition[]) => onChange(toWhens(next))

  if (!conditions) return <LockedPill />
  return (
    <>
      {conditions.map((condition, i) => (
        <span key={i} className="inline-flex max-w-full items-center gap-2">
          {i > 0 ? (
            <span className="text-xs text-neutral-400">{t(`${NS}.and`)}</span>
          ) : null}
          <ConditionPill
            id={`${id}-c${i}`}
            condition={condition}
            editable={editable}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onChange={(next) =>
              setConditions(conditions.map((c, k) => (k === i ? next : c)))
            }
            onRemove={() => setConditions(conditions.filter((_, k) => k !== i))}
          />
        </span>
      ))}
      {editable ? (
        <AddCondition
          ns={NS}
          n={n}
          empty={conditions.length === 0}
          groups={fieldGroups}
          searchLabel={t('warehouses.conditions.filter_by')}
          emptyText={t('warehouses.conditions.no_field')}
          width="w-64"
          {...bindOpen(`${id}:add`, openKey, setOpenKey)}
          onPick={(field) => {
            setConditions([
              ...conditions,
              newCondition(field as ConditionField)
            ])
            setOpenKey(`${id}-c${conditions.length}:value`)
          }}
        />
      ) : conditions.length === 0 ? (
        <span className="text-sm text-neutral-400">{t(`${NS}.always`)}</span>
      ) : null}
    </>
  )
}

// A column cell picking one of a few values, like the rule's warehouse.
export const PickCell: React.FC<{
  id: string
  label: string
  options: { value: string; label: string }[]
  value: string
  // Shown for an empty value, which would otherwise leave nothing to click.
  unsetLabel: string
  readOnly: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onPick: (value: string) => void
}> = ({
  id,
  label,
  options,
  value,
  unsetLabel,
  readOnly,
  openKey,
  setOpenKey,
  onPick
}) => {
  const className =
    'inline-flex w-48 shrink-0 items-center rounded-md px-2 py-1 text-left text-sm font-medium outline-none ' +
    (value ? 'text-neutral-900 ' : 'text-neutral-400 ') +
    (readOnly ? '' : 'hover:bg-neutral-100 focus-visible:bg-neutral-100')
  const content = value
    ? (options.find((o) => o.value === value)?.label ?? value)
    : unsetLabel
  if (readOnly) return <span className={className}>{content}</span>
  return (
    <OptionsPicker
      options={options}
      value={[value]}
      onChange={([picked]) => picked !== undefined && onPick(picked)}
      {...bindOpen(id, openKey, setOpenKey)}
      triggerLabel={label}
      triggerClassName={className}
      // The dashboard sets Roobert on spans, not on the picker's button.
      triggerContent={<span className="truncate">{content}</span>}
    />
  )
}
